import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import useTenant from '../hooks/useTenant';
import FundoMarca from '../components/FundoMarca';

const HZERO_LIME = '#CCFF00';

/** Logo do tenant (ou a marca "konq") + "CONCURSOS by hzero". */
export function AuthBrand({ sx }) {
  const { t } = useTranslation();
  const { branding, currentTenant } = useTenant();
  const logoUrl = branding?.logoUrl || currentTenant?.logoUrl || null;
  const nome = branding?.nome || currentTenant?.nome;

  return (
    <Box sx={{ textAlign: 'center', ...sx }}>
      {logoUrl ? (
        <Box
          component="img"
          src={logoUrl}
          alt={nome || 'Logo'}
          sx={{ maxHeight: 72, maxWidth: 240, objectFit: 'contain', mb: 1 }}
        />
      ) : (
        <Typography
          sx={{ fontSize: 42, fontWeight: 700, color: '#1A1A1A', lineHeight: 1, mb: 0.5, letterSpacing: -0.5 }}
        >
          konq
        </Typography>
      )}
      <Typography
        sx={{ color: 'text.secondary', letterSpacing: 2, textTransform: 'uppercase', fontSize: 10, fontWeight: 700 }}
      >
        {t('Contests')}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>
          by
        </Typography>
        <Box component="img" src="/assets/logo-hzero.png" alt="hzero" sx={{ height: 11 }} />
      </Box>
    </Box>
  );
}

/** Painel visual da esquerda: fundo de marca do tenant + acento na cor do cliente. */
function PainelVisual({ wide }) {
  const { t } = useTranslation();
  const { branding } = useTenant();
  const accent = branding?.corDestaque || branding?.corSecundaria || HZERO_LIME;

  return (
    <Box
      sx={{
        flex: wide ? { md: '0 0 34%', lg: '0 0 38%' } : 1,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'flex-end',
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <FundoMarca direcao="baixo" />

      {/* Linhas decorativas */}
      <Box sx={{ position: 'absolute', top: 40, left: 40, width: 120, height: '1px', bgcolor: accent, opacity: 0.5 }} />
      <Box sx={{ position: 'absolute', top: 40, left: 40, width: '1px', height: 80, bgcolor: accent, opacity: 0.5 }} />

      <Box sx={{ position: 'relative', zIndex: 1, px: 6, pb: 8, maxWidth: 520 }}>
        <Box sx={{ width: 40, height: 3, bgcolor: accent, borderRadius: 1, mb: 3 }} />
        <Typography variant="h3" color="white" sx={{ mb: 2, lineHeight: 1.2 }}>
          {t('auth.tagline.title')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.8 }}>
          {t('auth.tagline.subtitle')}
        </Typography>
      </Box>
    </Box>
  );
}

/**
 * Layout das telas fora do app logado (login, cadastro, convite, 404...).
 * Painel visual à esquerda (desktop); conteúdo à direita sobre fundo branco.
 * `wide` dá mais largura ao conteúdo (wizards de cadastro).
 */
export default function AuthLayout({ children, wide = false, footer }) {
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#fff' }}>
      <PainelVisual wide={wide} />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: { xs: 2, sm: 6 },
          py: { xs: 4, sm: 6 },
        }}
      >
        <AuthBrand sx={{ mb: { xs: 3, sm: 5 } }} />

        <Box
          sx={{
            width: '100%',
            maxWidth: wide ? 760 : 420,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: wide ? 'flex-start' : 'center',
          }}
        >
          {children}
          <Outlet />
        </Box>

        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          {footer}
          <Typography variant="caption" color="text.disabled">
            Konq Platform &copy; {new Date().getFullYear()}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
